import { Card } from "@general-backoffice/core";
import { FormDecoration } from "@general-backoffice/core/index";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card as BTCard, CardBody, Col, ListGroup, ListGroupItem, Row } from "reactstrap";

interface CheckedComponentProps {
  checked?: boolean;
  warning?: boolean;
}

const CheckedComponent: React.FC<CheckedComponentProps> = ({ checked, warning }) => {
  if (checked && warning) return <i className="fas fa-exclamation-circle" />;
  if (checked) return <i className="fas fa-check-circle" />;
  return <i className="fas fa-times-circle" />;
};

export interface RequirementItem {
  code?: string;
  text: string;
  checked?: boolean;
  warning?: boolean;
  color?: string | false;
}

interface RequirementsCardProps {
  requirements?: RequirementItem[] | RequirementItem[][];
  title: string;
  actionTitle?: string;
  onAction?: () => void;
  isLoading?: boolean;
  onInfo?: () => void;
  compact?: boolean;
}

const RequirementsCard: React.FC<RequirementsCardProps> = ({
  requirements = [],
  title,
  actionTitle,
  onAction,
  isLoading,
  onInfo,
  compact = false,
}) => {
  const { t } = useTranslation("referrer", { keyPrefix: "requirementsCard" });

  // Normalize to nested array format
  const multiRequirements: RequirementItem[][] = (() => {
    if (requirements.length === 0) return [];
    if (Array.isArray(requirements[0])) return requirements.filter(Array.isArray);
    return [requirements.filter((r): r is RequirementItem => !Array.isArray(r))];
  })();

  const CheckedRequired = multiRequirements.find((res) => !res.find((r) => !r.checked));

  const compactStyles = compact
    ? {
        item: { fontSize: "0.75rem" },
        icon: { fontSize: "0.7rem" },
        padding: { padding: "4px 0" },
      }
    : {
        item: { fontSize: "0.9rem" },
        icon: {},
        padding: {},
      };

  return (
    <BTCard>
      <Card.Header title={title}>
        {!!onInfo && (
          <Button className="h3 my-0 shadow-none" onClick={onInfo} style={compact ? { fontSize: "0.85rem" } : {}}>
            <i className="fas fa-info-circle" />
          </Button>
        )}
        {!!onAction && (
          <Button
            color={"default"}
            onClick={onAction}
            disabled={!CheckedRequired || isLoading}
            size={compact ? "sm" : undefined}
          >
            {actionTitle}
          </Button>
        )}
      </Card.Header>
      <CardBody style={compact ? { padding: "0.75rem" } : {}}>
        <ListGroup flush>
          {multiRequirements.map((groupRequirements, groupIndex) => {
            const groupKey = groupRequirements.map((r) => r.code || r.text).join("-") || `group-${groupIndex}`;
            return (
              <React.Fragment key={groupKey}>
                {groupIndex > 0 && <FormDecoration.LineTitle title={t("or")} />}
                <ListGroupItem className="border-0" style={compact ? { padding: "0.25rem 0" } : {}}>
                  <ListGroup flush>
                    {groupRequirements.map((item, itemIndex) => {
                      const itemKey = item.code || item.text || `item-${itemIndex}`;
                      return (
                        <ListGroupItem key={itemKey} className="border-0 py-1" style={compactStyles.padding}>
                          <Row
                            className={
                              "align-items-center " +
                              classNames({
                                "text-success": item?.checked,
                                "text-warning": item?.warning,
                                [`text-${item?.color}`]: item?.color,
                              })
                            }
                          >
                            <Col xs={"auto"} style={compactStyles.icon}>
                              <CheckedComponent checked={item?.checked} warning={item?.warning} />
                            </Col>
                            <Col style={compactStyles.item}>{item.text}</Col>
                          </Row>
                        </ListGroupItem>
                      );
                    })}
                  </ListGroup>
                </ListGroupItem>
              </React.Fragment>
            );
          })}
        </ListGroup>
      </CardBody>
    </BTCard>
  );
};

export default RequirementsCard;
